import React, { useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Gcp } from "../../types/gcp";
import { Resource } from "../../types/resource";
import { Site } from "../../types/site";
import SiteRectangle from "./SiteRectangle";
import HeavyMachineMarker from "./HeavyMachineMarker";
import Spinner from "../Spinner";
import { Settings } from "../../config/settings";
import ToggleButton from "./toggleButton";

import SearchForm from "./SearchForm";
import dayjs from "dayjs";
import ja from "dayjs/locale/ja";

dayjs.locale(ja);

type Props = {
  gcp: Gcp;
  resources: [Resource];
  sites: [Site];
  basePath: string;
  mapCenter: [number, number] | null;
};

const Map: React.FC<Props> = ({ gcp, resources, sites, basePath, mapCenter }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: gcp.apiKey,
    language: "ja",
  });

  const zoom = 10;
  const containerStyle = {
    height: "75vh",
    width: "100%",
  };

  const [showCompletedSites, setShowCompletedSites] = useState(false);
  const filteredSites = sites.filter((site) => {
    if (showCompletedSites) {
      return true;
    }
    return dayjs().isBefore(dayjs(site.completedOn));
  });

  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [selectedSubGrpNames, setSelectedSubGrpNames] = useState<string[]>([]);
  const renderedResources =
    selectedSubGrpNames.length == 0 && selectedResourceIds.length == 0
      ? resources
      : selectedSubGrpNames.reduce(
          (acc, grpName) => {
            if (acc.some((r) => r.subGroupName === grpName)) {
              return acc;
            }
            return [...acc, ...resources.filter((r) => r.subGroupName === grpName)];
          },
          resources.filter((r) => selectedResourceIds.includes(r.id)),
        );

  const calculateCenter = () => {
    const forCalulateResources = renderedResources.filter((resource) => resource.lat && resource.lon);

    if (forCalulateResources.length === 0) {
      // MEMO: 重機→現場→皇居の順で中心位置を決定
      return {
        lat: mapCenter?.[0] ?? Settings.centerOfMap.lat,
        lng: mapCenter?.[1] ?? Settings.centerOfMap.lon,
      };
    }

    const totalLat = forCalulateResources.reduce((sum, r) => sum + r.lat, 0);
    const totalLng = forCalulateResources.reduce((sum, r) => sum + r.lon, 0);

    return {
      lat: totalLat / forCalulateResources.length,
      lng: totalLng / forCalulateResources.length,
    };
  };

  const center = calculateCenter();

  const renderMap = () => (
    <>
      <div className="d-flex justify-content-center">
        <div className="search-form card">
          <div className="card-body">
            <div className="row">
              <div className="col-6">
                <SearchForm
                  title={"重機分類を選択する"}
                  placeholder={selectedSubGrpNames.length === 0 ? "分類を選択してください" : ""}
                  options={resources
                    .map((r) => ({
                      value: r.subGroupName,
                      label: r.subGroupName,
                    }))
                    .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i)}
                  onClick={(selectedNames) => {
                    setSelectedSubGrpNames(selectedNames);
                  }}
                />
              </div>
              <div className="col-6">
                <SearchForm
                  title={"重機を選択する"}
                  placeholder={selectedResourceIds.length === 0 ? "重機を選択してください" : ""}
                  options={resources
                    .filter(
                      (r) =>
                        selectedSubGrpNames.length == 0 ||
                        selectedSubGrpNames.includes(r.subGroupName) ||
                        selectedResourceIds.includes(r.id),
                    )
                    .map((r) => ({
                      value: r.id,
                      label: r.name,
                    }))}
                  onClick={(selectedIds) => {
                    setSelectedResourceIds(selectedIds);
                  }}
                />
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-12 border-0">
                <ToggleButton
                  label={"工期が終了した現場を表示する"}
                  handleChange={() => {
                    setShowCompletedSites(!showCompletedSites);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="row">
          <div className="col-12">
            <GoogleMap mapContainerStyle={containerStyle} clickableIcons={false} center={center} zoom={zoom}>
              {filteredSites.map((s) => (
                <SiteRectangle site={s} key={`site-${s.id}`} basePath={basePath} />
              ))}
              {renderedResources.map((r) => (
                <HeavyMachineMarker resource={r} key={`heavyMachine-${r.id}`} basePath={basePath} />
              ))}
            </GoogleMap>
          </div>
        </div>
      </div>
    </>
  );

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return isLoaded ? renderMap() : <Spinner />;
};

export default Map;
